import { LeaderboardResponse, User, LeaderboardEntry } from '../../../../common/types';
import { useColors } from '../../../../common/chat/userColors';
import { UserIcon } from '../../../../components/user/UserIcon';
import { Modal } from '../../../../components/Modal';
import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { getLeaderboard } from '../../../../common/api/scenarios';

export type LeaderboardViewProps = {
  leaderboard: LeaderboardResponse;
};

// Helper component to display team members with their assigned colors
type TeamMembersProps = {
  members: User[];
};

function TeamMembers({ members }: TeamMembersProps) {
  const authorColors = useColors([...members]);
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1">
      {members.map((member) => (
        <div key={member.userUuid} className="flex items-center gap-1">
          <UserIcon user={member} color={authorColors[member.userUuid]} />
        </div>
      ))}
    </div>
  );
}

// Helper component for the progress bar
type ProgressBarProps = {
  percentage: number;
  isComplete: boolean;
};

function ProgressBar({ percentage, isComplete }: ProgressBarProps) {
  return (
    <div className="w-full bg-gray-700 rounded-full h-2.5">
      <div
        className={`h-2.5 rounded-full ${isComplete ? 'bg-green-500' : 'bg-blue-500'}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
}

// Helper function to format duration
function formatDuration(milliseconds: number | undefined): string {
  if (!milliseconds) return 'Not started';

  // For very short durations (less than a minute)
  if (milliseconds < 60000) {
    return `${Math.round(milliseconds / 1000)}s`;
  }

  const hours = Math.floor(milliseconds / 3600000);
  const minutes = Math.floor((milliseconds % 3600000) / 60000);
  const seconds = Math.floor((milliseconds % 60000) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}

// Team Details Modal Component
type TeamDetailsModalProps = {
  team: LeaderboardEntry;
  isOpen: boolean;
  onClose: () => void;
};

function TeamDetailsModal({ team, isOpen, onClose }: TeamDetailsModalProps) {
  const authorColors = useColors([...team.members]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="max-w-2xl" color="bg-[#1a2a3a]">
      <div className="p-4 text-white">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-900 font-bold">
            #{team.rank}
          </span>
          Room {team.roomNumber} Details
        </h2>

        {/* Team Members */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Team Members</h3>
          <div className="flex flex-wrap gap-3">
            {team.members.map((member) => (
              <div
                key={member.userUuid}
                className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ backgroundColor: `${authorColors[member.userUuid]}40` }}
              >
                <UserIcon user={member} color={authorColors[member.userUuid]} />
                <span className="font-medium">{member.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Overview */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Progress Overview</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[#152232] p-3 rounded-lg">
              <div className="text-gray-400 text-sm">Status</div>
              <div className="font-medium">
                {team.isComplete ? (
                  <span className="text-green-400 whitespace-nowrap">Completed</span>
                ) : team.startedAt ? (
                  <span className="text-blue-400 whitespace-nowrap">In Progress</span>
                ) : (
                  <span className="text-gray-400 whitespace-nowrap">Not Started</span>
                )}
              </div>
            </div>

            <div className="bg-[#152232] p-3 rounded-lg">
              <div className="text-gray-400 text-sm">Completion</div>
              <div className="font-medium">
                {team.completedStages} of {team.totalStages} stages
              </div>
            </div>

            <div className="bg-[#152232] p-3 rounded-lg">
              <div className="text-gray-400 text-sm">Time</div>
              <div className="font-medium font-mono">{formatDuration(team.duration)}</div>
            </div>

            <div className="bg-[#152232] p-3 rounded-lg">
              <div className="text-gray-400 text-sm">Started</div>
              <div className="font-medium">
                {team.startedAt
                  ? formatDistanceToNow(new Date(team.startedAt), { addSuffix: true })
                  : 'Not started yet'}
              </div>
            </div>
          </div>
        </div>

        {/* Stage Progress */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Stage Progress</h3>
          <div className="space-y-3">
            {team.stages.map((stage) => (
              <div key={stage.stageId} className="bg-[#152232] p-3 rounded-lg">
                <div className="flex justify-between items-center gap-4">
                  <h4 className="font-medium">
                    Stage {stage.stageNumber}: {stage.stageName}
                  </h4>
                  {stage.completedAt ? (
                    <span className="px-2 py-1 bg-green-900 text-green-300 rounded-md text-xs whitespace-nowrap">
                      Completed
                    </span>
                  ) : stage.startedAt ? (
                    <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded-md text-xs whitespace-nowrap">
                      In Progress
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-md text-xs whitespace-nowrap">
                      Not Started
                    </span>
                  )}
                </div>

                {stage.startedAt && (
                  <div className="mt-1 text-sm text-gray-400">
                    {stage.completedAt ? (
                      <div className="flex justify-between">
                        <span>Completed in {formatDuration(stage.duration)}</span>
                        <span>
                          {formatDistanceToNow(new Date(stage.completedAt), { addSuffix: true })}
                        </span>
                      </div>
                    ) : (
                      <div>
                        Started{' '}
                        {formatDistanceToNow(new Date(stage.startedAt), { addSuffix: true })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function LeaderboardView(props: LeaderboardViewProps) {
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse>(props.leaderboard);

  const getReq = useMutation({
    mutationFn: () => {
      return getLeaderboard(props.leaderboard.scenarioUuid);
    },
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (JSON.stringify(leaderboard) !== JSON.stringify(data)) {
        setLeaderboard(data);
      }
    },
  });

  // recurring poll
  useEffect(() => {
    const pollInterval = setInterval(async () => {
      await getReq.mutateAsync();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getReq, props.leaderboard]);

  return (
    <div className="home-page bg-[#101c2c] text-white font-sans flex flex-col">
      <div className="p-4 lg:p-6 lg:max-w-6xl grid grid-cols-1 gap-4 mx-auto">
        {/* Header Section */}
        <div>
          <h1 className="text-4xl font-bold mb-2">Leaderboard for {leaderboard.scenarioName}</h1>
          <p className="text-gray-400 text-lg">
            Track team progress and completion times across {leaderboard.totalStages} stages
          </p>
        </div>

        {/* Leaderboard Table */}
        <div className="overflow-x-auto w-full">
          <table className="bg-[#1a2a3a] rounded-lg shadow-xl min-w-full min-h-full table-auto border-collapse">
            {/* Table Header */}
            <thead>
              <tr className="border-b border-gray-700 bg-[#152232] font-medium p-4">
                <th className="p-4 text-center">Position</th>
                <th className="p-4 text-center">Room #</th>
                <th className="p-4 text-left">Team</th>
                <th className="p-4 text-left">Progress</th>
                <th className="p-4 text-left">Time</th>
                <th className="p-4 text-left">Status</th>
              </tr>
            </thead>
            {/* Table Body */}
            <tbody className="divide-y divide-gray-700">
              {leaderboard.leaderboard.map((entry) => (
                <LeaderboardRow key={entry.roomUuid} entry={entry} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

type LeaderboardRowProps = {
  entry: LeaderboardEntry;
};

function LeaderboardRow(props: LeaderboardRowProps) {
  const { entry } = props;
  const [open, setOpen] = useState(false);

  function handleRowClick() {
    if (!open) {
      setOpen(true);
    }
  }

  return (
    <tr
      key={entry.roomUuid}
      className="hover:bg-[#1f3346] transition-colors cursor-pointer"
      onClick={handleRowClick}
    >
      {/* Rank */}
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-900 font-bold">
          #{entry.rank}
        </span>
      </td>

      {/* Room Number */}
      <td className="px-4 py-3 text-center font-medium">{entry.roomNumber}</td>

      {/* Team Members */}
      <td className="px-4 py-3">
        <TeamMembers members={entry.members} />
      </td>

      {/* Progress */}
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <ProgressBar percentage={entry.progressPercentage} isComplete={entry.isComplete} />
          <span className="text-xs text-gray-400">
            {entry.completedStages} of {entry.totalStages} stages completed
          </span>
        </div>
      </td>

      {/* Time */}
      <td className="px-4 py-3">
        <span className={`font-mono ${entry.isComplete ? 'text-green-400' : 'text-blue-400'}`}>
          {formatDuration(entry.duration)}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        {entry.isComplete ? (
          <span className="px-2 py-1 bg-green-900 text-green-300 rounded-md text-sm whitespace-nowrap">
            Completed
          </span>
        ) : entry.startedAt ? (
          <span className="px-2 py-1 bg-blue-900 text-blue-300 rounded-md text-sm whitespace-nowrap">
            In Progress
          </span>
        ) : (
          <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-md text-sm whitespace-nowrap">
            Not Started
          </span>
        )}
      </td>

      <TeamDetailsModal team={entry} isOpen={open} onClose={() => setOpen(false)} />
    </tr>
  );
}
